import { concatBytes } from '@casa/util';
import { NalType } from './h264.ts';
import type { RtpPacket } from '../rtsp/rtp.ts';

/** One coded picture. */
export interface AccessUnit {
    /** RTP timestamp in clock units, counted from the first packet and unwrapped past 32 bits. */
    timestamp: number;
    /** True for an IDR picture: decoding can start here. */
    key: boolean;
    /** NAL units without start codes. Parameter sets and access unit delimiters are taken out. */
    nalus: Uint8Array[];
    /** When the last packet of the picture arrived (`wallNow()`), if the caller said so. */
    receivedAt?: number;
    /** The parameter sets in force; present on key pictures only. */
    sps?: Uint8Array;
    pps?: Uint8Array;
}

export interface DepacketizerStats {
    packets: number;
    /** Packets missing according to the sequence numbers. */
    lostPackets: number;
    /** Packets with a payload format that is not handled (anything but single NAL, STAP-A, FU-A). */
    unsupportedPackets: number;
    accessUnits: number;
    keyFrames: number;
    /** Pictures not handed on: damaged by loss, or waiting for the next key picture. */
    droppedAccessUnits: number;
}

export interface H264DepacketizerOptions {
    /** Parameter sets from the SDP, used until the stream sends its own. */
    sps?: Uint8Array | undefined;
    pps?: Uint8Array | undefined;
    onAccessUnit(unit: AccessUnit) : void;
}

interface Building {
    rtpTimestamp: number;
    timestamp: number;
    key: boolean;
    damaged: boolean;
    nalus: Uint8Array[];
    receivedAt: number | undefined;
}

/**
 * Turns RTP packets with an H.264 payload (RFC 6184: single NAL unit, STAP-A, FU-A) into access
 * units. A picture ends at the marker bit or when the timestamp changes. After packet loss the
 * damaged picture and everything up to the next key picture is dropped, because those pictures
 * would be decoded against references that are missing.
 */
export class H264Depacketizer {
    readonly stats: DepacketizerStats = {
        packets: 0,
        lostPackets: 0,
        unsupportedPackets: 0,
        accessUnits: 0,
        keyFrames: 0,
        droppedAccessUnits: 0,
    };

    #sps: Uint8Array | undefined;
    #pps: Uint8Array | undefined;
    readonly #onAccessUnit: (unit: AccessUnit) => void;
    #current: Building | undefined;
    #fragments: Uint8Array[] | undefined;
    #lastSequence: number | undefined;
    #lastRtpTimestamp: number | undefined;
    #timestamp = 0;
    #needKey = true;
    #damageNext = false;

    constructor(options: H264DepacketizerOptions) {
        this.#sps = options.sps;
        this.#pps = options.pps;
        this.#onAccessUnit = options.onAccessUnit;
    }

    /** `receivedAt` is when the packet arrived (`wallNow()`); it is passed on with the picture. */
    push(packet: RtpPacket, receivedAt?: number) : void {
        this.stats.packets++;
        if (this.#lastSequence !== undefined) {
            const gap = (packet.sequenceNumber - this.#lastSequence - 1) & 0xffff;
            // A "gap" of more than half the number space is a late or repeated packet.
            if (gap >= 0x8000) {
                return;
            }
            if (gap > 0) {
                this.stats.lostPackets += gap;
                // The missing packets may be the end of this picture and the start of the next one.
                if (this.#current) {
                    this.#current.damaged = true;
                }
                this.#fragments = undefined;
                this.#damageNext = true;
            }
        }
        this.#lastSequence = packet.sequenceNumber;

        if (this.#current && this.#current.rtpTimestamp !== packet.timestamp) {
            this.flush();
        }
        if (!this.#current) {
            if (this.#lastRtpTimestamp !== undefined) {
                // `| 0` reads the 32-bit difference as signed, which unwraps the RTP timestamp.
                this.#timestamp += (packet.timestamp - this.#lastRtpTimestamp) | 0;
            }
            this.#lastRtpTimestamp = packet.timestamp;
            this.#current = {
                rtpTimestamp: packet.timestamp,
                timestamp: this.#timestamp,
                key: false,
                damaged: this.#damageNext,
                nalus: [],
                receivedAt,
            };
            this.#damageNext = false;
        }

        this.#current.receivedAt = receivedAt;
        this.#addPayload(this.#current, packet.payload);
        if (packet.marker) {
            this.flush();
        }
    }

    #addPayload(unit: Building, payload: Uint8Array) : void {
        if (payload.length === 0) {
            return;
        }

        const indicator = payload[0]!;
        const type = indicator & 0x1f;

        if (type === NalType.FuA) {
            if (payload.length < 2) {
                return;
            }

            const fuHeader = payload[1]!;
            if (fuHeader & 0x80) {
                // Start: rebuild the NAL header from the indicator (NRI) and the FU header (type).
                this.#fragments = [Uint8Array.of((indicator & 0xe0) | (fuHeader & 0x1f)), payload.subarray(2)];
            } else if (this.#fragments) {
                this.#fragments.push(payload.subarray(2));
            } else {
                unit.damaged = true; // middle of a NAL unit whose start was never seen
                return;
            }
            if (fuHeader & 0x40) {
                this.#addNal(unit, concatBytes(this.#fragments));
                this.#fragments = undefined;
            }
            return;
        }

        if (this.#fragments) {
            // A fragmented NAL unit that never got its end.
            this.#fragments = undefined;
            unit.damaged = true;
        }
        if (type >= 1 && type <= 23) {
            this.#addNal(unit, payload);
        } else if (type === NalType.StapA) {
            const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
            let offset = 1;
            while (offset + 2 <= payload.length) {
                const size = view.getUint16(offset);
                offset += 2;
                if (offset + size > payload.length) {
                    unit.damaged = true;
                    break;
                }
                this.#addNal(unit, payload.subarray(offset, offset + size));
                offset += size;
            }
        } else {
            this.stats.unsupportedPackets++;
        }
    }

    #addNal(unit: Building, nal: Uint8Array) : void {
        if (nal.length === 0) {
            return;
        }

        switch (nal[0]! & 0x1f) {
            case NalType.Sps:
                this.#sps = nal.slice();

                return;
            case NalType.Pps:
                this.#pps = nal.slice();

                return;
            case NalType.AccessUnitDelimiter:
                return;
            case NalType.Idr:
                unit.key = true;
                break;
        }
        unit.nalus.push(nal);
    }

    /** Ends the picture being assembled. Call it when the stream stops, to get the last picture. */
    flush() : void {
        const unit = this.#current;
        this.#current = undefined;
        this.#fragments = undefined;
        if (!unit) {
            return;
        }
        if (unit.damaged) {
            this.#needKey = true;
            this.stats.droppedAccessUnits++;

            return;
        }
        // Packets that carried only parameter sets do not make a picture.
        if (unit.nalus.length === 0) {
            return;
        }

        const ready = unit.key ? this.#sps !== undefined && this.#pps !== undefined : !this.#needKey;
        if (!ready) {
            this.stats.droppedAccessUnits++;

            return;
        }
        this.stats.accessUnits++;
        const result: AccessUnit = { timestamp: unit.timestamp, key: unit.key, nalus: unit.nalus };
        if (unit.receivedAt !== undefined) {
            result.receivedAt = unit.receivedAt;
        }
        if (unit.key) {
            this.#needKey = false;
            this.stats.keyFrames++;
            result.sps = this.#sps!;
            result.pps = this.#pps!;
        }
        this.#onAccessUnit(result);
    }
}
